import React, { useEffect, useState } from 'react';

import {
    BasePlanLaunchDate,
    GetBasePlanLaunchDatesInput,
    GetBasePlanLaunchDatesOutput,
    GetPaperPlanMetadataInput,
    GetPaperPlanMetadataOutput,
    PaperPlanMetadata
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DatePicker from '@amzn/meridian/date-picker';
import Heading from '@amzn/meridian/heading';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Textarea from '@amzn/meridian/textarea';

import { isEqual } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { TerritoryFlag } from 'src/components/TerritoryFlag';
import { LOCALE_EN_CA } from 'src/constants';
import { GLOBAL_TERRITORY } from 'src/constants/context';
import { IS_REQUIRED } from 'src/constants/errorMessages';
import { AppContext } from 'src/contexts/appContext';
import { initialAddPaperPlanMappingState } from 'src/data/PaperPlanMappingData';
import { IBasePlan } from 'src/types/IBasePlanLaunchDate';
import { IPaperPlanMapping } from 'src/types/IPPRPlanMappings';
import { IPaperPlanName } from 'src/types/IPaperPlan';
import { validateExactLength, validateRequired } from 'src/util/validationsUtil';

import { BasePlanIdSelect } from '../../BasePlanIdSelect';

export interface IPaperPlanMappingDetailsProps {
    paperPlanMapping: IPaperPlanMapping;
    setPaperPlanMapping: React.Dispatch<React.SetStateAction<IPaperPlanMapping>>;
    onNext: () => void;
    onCancel: () => void;
    isEditMode: boolean;
}

export const PaperPlanMappingDetails = ({
    paperPlanMapping,
    setPaperPlanMapping,
    onNext,
    onCancel,
    isEditMode
}: IPaperPlanMappingDetailsProps) => {
    const [paperPlanNames, setPaperPlanNames] = useState<IPaperPlanName[]>([]);
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});
    const [isFormValid, setIsFormValid] = useState(false);

    const { metadata, setTopAlertMsg } = React.useContext(AppContext);
    const territories = React.useMemo(
        () =>
            Object.values(metadata.territoryMapping).filter(
                (territory) => territory.territoryCode !== GLOBAL_TERRITORY.NAME
            ),
        [metadata.territoryMapping]
    );
    const [filteredTerritories, setFilteredTerritories] = useState(territories);
    const [basePlanLaunchDates, setBasePlanLaunchDates] = useState<BasePlanLaunchDate[]>([]);

    useEffect(() => {
        setIsFormValid(checkFormValidity());
    }, [paperPlanMapping, errorMessages]);

    useEffect(() => {
        if (paperPlanMapping.basePlanId && basePlanLaunchDates.length > 0) {
            const compatibleTerritories = getCompatibleTerritories(paperPlanMapping.basePlanId);
            setFilteredTerritories(compatibleTerritories);
        } else {
            setFilteredTerritories(territories);
        }
    }, [paperPlanMapping.basePlanId, territories, basePlanLaunchDates]);

    useEffect(() => {
        contractConfigurationClient
            .getPaperPlanMetadata(
                GetPaperPlanMetadataInput.clone({
                    includeDraftPlans: true,
                    serviceIds: [1]
                })
            )
            .subscribe(
                (response: GetPaperPlanMetadataOutput) => {
                    setPaperPlanNames(mapPaperPlansToPlanNames(response.planMetadata || []));
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('createEntity unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
        contractConfigurationClient.getBasePlanLaunchDates(GetBasePlanLaunchDatesInput.clone({})).subscribe(
            (response: GetBasePlanLaunchDatesOutput) => {
                const activeBasePlanLaunchDates = (response.basePlanLaunchDates || []).filter((launchDate) => {
                    const now = new Date();
                    const endDate = launchDate.endDate ? new Date(launchDate.endDate) : null;

                    return !endDate || endDate >= now;
                });
                setBasePlanLaunchDates(activeBasePlanLaunchDates);
            },
            (error: UnknownCoralError) => {
                console.error('getBasePlanLaunchDates error', error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            }
        );
    }, []);

    const mapPaperPlansToPlanNames = (planMetadata: PaperPlanMetadata[]) => {
        return planMetadata
            .map(
                (paperPlanMetadata: PaperPlanMetadata) =>
                    ({
                        paperPlanId: paperPlanMetadata.paperPlan?.paperPlanId,
                        paperPlanName: paperPlanMetadata.paperPlan?.paperPlanName || ''
                    }) as IPaperPlanName
            )
            .sort((a, b) => a.paperPlanName?.localeCompare(b.paperPlanName));
    };

    const handleOnChange = (field: string, value: string | number[]) => {
        setPaperPlanMapping((prevState) => ({
            ...prevState,
            [field]: value
        }));
    };

    const handlePaperPlanIdChange = (value: string) => {
        handleOnChange('paperPlanId', value);
        const paperPlanNameObj = paperPlanNames.find((paperPlan) => paperPlan.paperPlanId === parseInt(value));
        handleOnChange('paperPlanName', paperPlanNameObj?.paperPlanName || '');
    };

    const handleBasePlanChange = (basePlan: IBasePlan) => {
        setPaperPlanMapping((prevState) => ({
            ...prevState,
            basePlanName: basePlan.basePlanName || '',
            basePlanId: basePlan.basePlanId
        }));
    };

    const checkFormValidity = () => {
        const requiredFields = [
            'paperPlanId',
            'basePlanName',
            'territoryId',
            'territoryIds',
            'startDate',
            'description'
        ];

        for (const field of requiredFields) {
            if (field === 'territoryIds' || field === 'territoryId') {
                if (!isEditMode) {
                    if (field === 'territoryIds' && paperPlanMapping.territoryIds.length < 1) {
                        return false;
                    }
                } else {
                    if (field === 'territoryId' && paperPlanMapping.territoryId === undefined) {
                        return false;
                    }
                }
            } else if (!paperPlanMapping[field as keyof IPaperPlanMapping]) {
                return false;
            }
        }

        return isEqual(errorMessages, {}) && !isEqual(paperPlanMapping, initialAddPaperPlanMappingState);
    };

    const validateField = (field: string) => {
        let errorMessage = '';

        switch (field) {
            case 'paperPlanId':
                if (paperPlanMapping[field] === null) {
                    errorMessage = IS_REQUIRED;
                }
                break;
            case 'basePlanName':
            case 'description':
                if (!validateRequired(paperPlanMapping[field])) {
                    errorMessage = IS_REQUIRED;
                }
                break;
            case 'startDate':
                if (!validateRequired(paperPlanMapping.startDate?.replaceAll('-', ''))) {
                    errorMessage = IS_REQUIRED;
                }
                break;
            case 'territoryIds':
                if (paperPlanMapping.territoryIds.length < 1) {
                    errorMessage = IS_REQUIRED;
                }
        }

        if ((field === 'startDate' || field === 'endDate') && !errorMessage) {
            let endDateErrorMessage = '';
            const startDate = paperPlanMapping.startDate?.replaceAll('-', '');
            const endDate = paperPlanMapping.endDate?.replaceAll('-', '');
            if (endDate && validateExactLength(endDate, 8) && endDate <= startDate) {
                endDateErrorMessage = 'Mapping end date must be after mapping start date';
            }
            setErrorMessages((prevState) => {
                const newState = { ...prevState };
                if (!endDateErrorMessage) {
                    delete newState.endDate;
                } else {
                    newState.endDate = endDateErrorMessage;
                }
                return newState;
            });
            if (field === 'endDate') return;
        }

        setErrorMessages((prevState) => {
            const newState = { ...prevState };
            if (errorMessage === '') {
                delete newState[field];
            } else {
                newState[field] = errorMessage;
            }
            return newState;
        });
    };

    const getCompatibleTerritories = React.useCallback(
        (basePlanId: number) => {
            if (!basePlanId || basePlanLaunchDates.length === 0) {
                return territories;
            }

            const compatibleTerritoryIds = basePlanLaunchDates
                .filter((launchDate) => launchDate.basePlan?.basePlanId === basePlanId)
                .map((launchDate) => launchDate.territory);

            return territories.filter((territory) => compatibleTerritoryIds.includes(territory.territoryCode!));
        },
        [basePlanLaunchDates, territories]
    );

    return (
        <>
            <Heading level={1}>{isEditMode ? 'Edit details' : 'Mapping Details'}</Heading>
            <Column spacingInset="450 none 500 none" spacing="450">
                <Select
                    label="PAPER plan ID *"
                    value={paperPlanMapping.paperPlanId}
                    onChange={(value) => handlePaperPlanIdChange(value)}
                    onBlur={() => validateField('paperPlanId')}
                    error={!!errorMessages.paperPlanId}
                    errorMessage={errorMessages.paperPlanId}
                    size="xlarge"
                >
                    {paperPlanNames.map((paperPlanName) => (
                        <SelectOption
                            value={paperPlanName.paperPlanId}
                            label={paperPlanName.paperPlanName}
                            key={paperPlanName.paperPlanId}
                        />
                    ))}
                </Select>
                <BasePlanIdSelect
                    basePlanId={paperPlanMapping.basePlanId}
                    onChange={handleBasePlanChange}
                    validateField={() => validateField('basePlanName')}
                    errorMessage={errorMessages.basePlanName}
                />
                <Input
                    label="Enter base plan promo"
                    value={paperPlanMapping.basePlanPromoId}
                    onChange={(value) => handleOnChange('basePlanPromoId', value)}
                    onBlur={() => validateField('basePlanPromoId')}
                    error={!!errorMessages.basePlanPromoId}
                    errorMessage={errorMessages.basePlanPromoId}
                    size="xlarge"
                />
                {isEditMode ? (
                    <Select
                        label="Territory *"
                        value={paperPlanMapping.territoryId}
                        onChange={(value) => handleOnChange('territoryId', value)}
                        onBlur={() => validateField('territoryId')}
                        error={!!errorMessages.territoryId}
                        errorMessage={errorMessages.territoryId}
                        size="xlarge"
                    >
                        {filteredTerritories.map((territory) => (
                            <SelectOption
                                value={territory.territoryId!}
                                label={territory.territoryCode!}
                                key={territory.territoryId}
                            >
                                {() => <TerritoryFlag territoryCode={territory.territoryCode!} />}
                            </SelectOption>
                        ))}
                    </Select>
                ) : (
                    <Select
                        label="Territory *"
                        value={paperPlanMapping.territoryIds}
                        onChange={(value) => handleOnChange('territoryIds', value)}
                        onBlur={() => validateField('territoryIds')}
                        error={!!errorMessages.territoryIds}
                        errorMessage={errorMessages.territoryIds}
                        size="xlarge"
                    >
                        {filteredTerritories.map((territory) => (
                            <SelectOption
                                value={territory.territoryId!}
                                label={territory.territoryCode!}
                                key={territory.territoryId}
                            >
                                {() => <TerritoryFlag territoryCode={territory.territoryCode!} />}
                            </SelectOption>
                        ))}
                    </Select>
                )}
                <Row widths="fill" alignmentVertical="top">
                    <DatePicker
                        label="Start date *"
                        value={paperPlanMapping.startDate}
                        onChange={(value) => handleOnChange('startDate', value)}
                        onBlur={() => validateField('startDate')}
                        error={!!errorMessages.startDate}
                        errorMessage={errorMessages.startDate}
                        locale={LOCALE_EN_CA}
                        size="xlarge"
                    />
                    <DatePicker
                        label="End date"
                        value={paperPlanMapping.endDate}
                        onChange={(value) => handleOnChange('endDate', value)}
                        onBlur={() => validateField('endDate')}
                        error={!!errorMessages.endDate}
                        errorMessage={errorMessages.endDate}
                        locale={LOCALE_EN_CA}
                        size="xlarge"
                    />
                </Row>
                <Textarea
                    label="Description of the plan or promotion *"
                    value={paperPlanMapping.description}
                    onChange={(value) => handleOnChange('description', value)}
                    onBlur={() => validateField('description')}
                    error={!!errorMessages.description}
                    errorMessage={errorMessages.description}
                    resize="none"
                    placeholder="Ex...2023 spring sitewide 3 for 99 price drop for US/GB/DE/FR/IT/ES"
                />
            </Column>
            <Row alignmentHorizontal="end">
                <Button type="link" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="primary" onClick={onNext} disabled={!isFormValid}>
                    Next
                </Button>
            </Row>
        </>
    );
};
